import React, { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DndContext,
  DragEndEvent,
  closestCorners,
} from '@dnd-kit/core'
import { DragOverlay } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useAuth } from '../context/AuthContext'
import { Card } from '../components/common/Card'
import { AssignTaskModal } from '../components/common/AssignTaskModal'
import { Button } from '../components/common/Button'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { isStatusFilterSelected, StatusPicker, useStatusPickerSelection } from '../components/common/StatusPicker'
import { TaskViewDialog } from '../components/features/TaskViewDialog'
import {
  KANBAN_GROUP_BY_LABELS,
  KANBAN_GROUP_BY_OPTIONS,
  isTaskPrerequisiteReady,
  TASK_STATUSES,
  TASK_STATUS_LABELS,
  TASK_TYPES,
  TASK_TYPE_LABELS,
} from '../lib/config/project-config'
import { isSalesLikeRole } from '../lib/config/role-config'
import { TaskConcurrencyError, mutations } from '../lib/supabase/mutations'
import { queries } from '../lib/supabase/queries'
import { RelatedAppRecord, Task } from '../lib/types/database.types'
import { getWorkflowActionForStatusChange, transitionTask, WorkflowAction } from '../lib/workflow/taskWorkflow'
import { sendTaskAssignedEmailIfNeeded } from '../utils/emailNotifications'
import { getTaskCurrentStatusDueDate } from '../utils/statusDates'
import KanbanColumn from '../components/kanban/KanbanColumn'
import KanbanCard from '../components/kanban/KanbanCard'

type GroupBy = (typeof KANBAN_GROUP_BY_OPTIONS)[number]
const KANBAN_MY_TASKS_STORAGE_KEY = 'devtracker-kanban-my-tasks-only'
const DATE_BUCKET_ORDER = ['today', 'this_week', 'next_week', 'future', 'no_date'] as const
const getDateBucket = (projectedCompletion: string | null): (typeof DATE_BUCKET_ORDER)[number] => {
  if (!projectedCompletion) {
    return 'no_date'
  }

  const [year, month, day] = projectedCompletion.split('-').map(Number)
  const dueDate = new Date(year, (month || 1) - 1, day || 1)
  const today = new Date()
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  const dayOfWeek = startOfToday.getDay()
  const daysUntilMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1
  const startOfWeek = new Date(startOfToday)
  startOfWeek.setDate(startOfToday.getDate() - daysUntilMonday)

  const startOfNextWeek = new Date(startOfWeek)
  startOfNextWeek.setDate(startOfWeek.getDate() + 7)

  const startOfFollowingWeek = new Date(startOfNextWeek)
  startOfFollowingWeek.setDate(startOfNextWeek.getDate() + 7)

  if (dueDate.getTime() === startOfToday.getTime()) {
    return 'today'
  }

  if (dueDate >= startOfToday && dueDate < startOfNextWeek) {
    return 'this_week'
  }

  if (dueDate >= startOfNextWeek && dueDate < startOfFollowingWeek) {
    return 'next_week'
  }

  return 'future'
}

const getColumnKey = (task: Task, groupBy: GroupBy): string => {
  switch (groupBy) {
    case 'status':
      return task.status
    case 'assigned_to':
      return task.assigned_to || 'unassigned'
    case 'type':
      return task.type
    case 'related_app':
      return task.related_app_id || 'unknown_app'
    case 'date':
      return getDateBucket(getTaskCurrentStatusDueDate(task))
    default:
      return 'unknown'
  }
}

const getColumnLabel = (key: string, groupBy: GroupBy, profiles: Map<string, string>, relatedApps: Map<string, RelatedAppRecord>): string => {
  switch (groupBy) {
    case 'status':
      return TASK_STATUS_LABELS[key as keyof typeof TASK_STATUS_LABELS] || key
    case 'assigned_to':
      return key === 'unassigned' ? 'Unassigned' : profiles.get(key) || 'Unknown'
    case 'type':
      return TASK_TYPE_LABELS[key as keyof typeof TASK_TYPE_LABELS] || key
    case 'related_app':
      return key === 'unknown_app' ? 'Unknown App' : relatedApps.get(key)?.name || 'Unknown App'
    case 'date':
      if (key === 'today') return 'Today'
      if (key === 'this_week') return 'This Week'
      if (key === 'next_week') return 'Next Week'
      if (key === 'future') return 'Future'
      if (key === 'no_date') return 'No Date'
      return key
    default:
      return key
  }
}

const getOrderedColumnEntries = (grouped: Map<string, Task[]>, groupBy: GroupBy) => {
  if (groupBy === 'status') {
    return TASK_STATUSES.filter((status) => grouped.has(status)).map((status) => [status, grouped.get(status) || []] as const)
  }

  if (groupBy === 'type') {
    return TASK_TYPES.filter((type) => grouped.has(type)).map((type) => [type, grouped.get(type) || []] as const)
  }

  if (groupBy === 'related_app') {
    return Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b))
  }

  if (groupBy === 'date') {
    return DATE_BUCKET_ORDER.filter((bucket) => grouped.has(bucket)).map((bucket) => [bucket, grouped.get(bucket) || []] as const)
  }

  return Array.from(grouped.entries()).sort(([a], [b]) => a.localeCompare(b))
}

export const KanbanPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { user, profile } = useAuth()
  const [groupBy, setGroupBy] = useState<GroupBy>('status')
  const [assigningTask, setAssigningTask] = useState<Task | null>(null)
  const [selectedAssignee, setSelectedAssignee] = useState('')
  const [assignError, setAssignError] = useState<string | null>(null)
  const [isViewOnly, setIsViewOnly] = useState(false)
  const [viewingTask, setViewingTask] = useState<Task | null>(null)
  const [workflowError, setWorkflowError] = useState<string | null>(null)
  const { selectedStatuses, selectedStatusSet, toggleStatus } = useStatusPickerSelection()
  const [myTasksOnly, setMyTasksOnly] = useState<boolean>(() => {
    if (typeof window === 'undefined') {
      return true
    }

    const storedValue = window.localStorage.getItem(KANBAN_MY_TASKS_STORAGE_KEY)
    return storedValue ? storedValue === 'true' : true
  })

  const tasksQuery = useQuery({
    queryKey: ['tasks', 'kanban'],
    queryFn: () => queries.getTasks(false),
  })

  const profilesQuery = useQuery({
    queryKey: ['profiles', 'all'],
    queryFn: queries.getAllProfiles,
  })

  const relatedAppsQuery = useQuery({
    queryKey: ['related-apps'],
    queryFn: queries.getRelatedApps,
  })

  const updateTaskMutation = useMutation({
    mutationFn: ({ taskId, updates, expectedUpdatedAt }: { taskId: string; updates: Parameters<typeof mutations.updateTask>[1]; expectedUpdatedAt?: string }) => {
      if (expectedUpdatedAt) {
        return mutations.updateTaskWithExpectedUpdatedAt(taskId, expectedUpdatedAt, updates)
      }

      return mutations.updateTask(taskId, updates)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['tasks'] })
    },
  })

  const taskPrerequisitesQuery = useQuery({
    queryKey: ['task-prerequisites'],
    queryFn: queries.getTaskPrerequisites,
  })

  const tasks = tasksQuery.data || []
  const profiles = profilesQuery.data || []
  const relatedApps = relatedAppsQuery.data || []

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    window.localStorage.setItem(KANBAN_MY_TASKS_STORAGE_KEY, String(myTasksOnly))
  }, [myTasksOnly])

  // Filter tasks
  const filteredTasks = useMemo(() => {
    let result = [...tasks]

    result = result.filter((task) => isStatusFilterSelected(task.status, selectedStatusSet))
    
    // My Tasks filtering - role-dependent
    if (myTasksOnly && user) {
      if (isSalesLikeRole(profile?.role)) {
        const directlyMatchedTasks = result.filter((task) => task.created_by === user.id || task.assigned_to === user.id)
        const visibleParentIds = new Set(
          directlyMatchedTasks.filter((task) => !task.parent_task_id).map((task) => task.id)
        )

        result = result.filter(
          (task) =>
            task.created_by === user.id ||
            task.assigned_to === user.id ||
            (Boolean(task.parent_task_id) && visibleParentIds.has(task.parent_task_id!))
        )
      } else {
        // Dev & Admin users: My Tasks = tasks assigned to them
        result = result.filter((task) => task.assigned_to === user.id)
      }

      // My Tasks should never include unassigned records.
      result = result.filter((task) => Boolean(task.assigned_to))
    }
    return result
  }, [tasks, selectedStatusSet, myTasksOnly, user, profile])

  // Create profile lookup map
  const profileMap = useMemo(
    () => new Map(profiles.map((p) => [p.id, p.full_name || p.email || 'Unknown User'])),
    [profiles]
  )
  const relatedAppMap = useMemo(
    () => new Map(relatedApps.map((app) => [app.id, app])),
    [relatedApps]
  )
  const prerequisiteByTaskId = useMemo(
    () => new Map((taskPrerequisitesQuery.data || []).map((item) => [item.task_id, item.prerequisite_task_id])),
    [taskPrerequisitesQuery.data]
  )
  const getTaskReadinessState = (task: Task): 'ready' | 'blocked' | null => {
    if (!task.parent_task_id) {
      return null
    }

    const prerequisiteTaskId = prerequisiteByTaskId.get(task.id)
    if (!prerequisiteTaskId) {
      return 'ready'
    }

    const prerequisiteTask = tasks.find((item) => item.id === prerequisiteTaskId)
    return isTaskPrerequisiteReady(prerequisiteTask?.status) ? 'ready' : 'blocked'
  }

  // Group tasks by selected column
  const columns = useMemo(() => {
    const grouped = new Map<string, Task[]>()

    // Initialize all columns based on groupBy type
    if (groupBy === 'status') {
      selectedStatuses.forEach((status) => {
        grouped.set(status, [])
      })
    } else if (groupBy === 'type') {
      TASK_TYPES.forEach((type) => {
        grouped.set(type, [])
      })
    } else if (groupBy === 'date') {
      DATE_BUCKET_ORDER.forEach((bucket) => {
        grouped.set(bucket, [])
      })
    } else {
      // For assigned_to and related_app, we discover columns from tasks
      filteredTasks.forEach((task) => {
        const key = getColumnKey(task, groupBy)
        if (!grouped.has(key)) {
          grouped.set(key, [])
        }
      })
    }

    // Populate tasks into columns
    filteredTasks.forEach((task) => {
      const key = getColumnKey(task, groupBy)
      if (!grouped.has(key)) {
        grouped.set(key, [])
      }
      grouped.get(key)!.push(task)
    })

    // Sort tasks within each column by priority (high to low) then alphabetically
    const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 }
    grouped.forEach((columnTasks) => {
      columnTasks.sort((a, b) => {
        const priorityDiff = (PRIORITY_ORDER[a.priority as keyof typeof PRIORITY_ORDER] ?? 999) - 
                            (PRIORITY_ORDER[b.priority as keyof typeof PRIORITY_ORDER] ?? 999)
        if (priorityDiff !== 0) return priorityDiff
        return a.title.localeCompare(b.title)
      })
    })

    return getOrderedColumnEntries(grouped, groupBy).slice(0, 10)
  }, [filteredTasks, groupBy, selectedStatuses])

  const isLoading = tasksQuery.isLoading || profilesQuery.isLoading || relatedAppsQuery.isLoading || taskPrerequisitesQuery.isLoading
  const error = tasksQuery.error || profilesQuery.error || relatedAppsQuery.error || taskPrerequisitesQuery.error

  const openAssignModal = (task: Task) => {
    if (isSalesLikeRole(profile?.role)) {
      return
    }

    setAssigningTask(task)
    setSelectedAssignee(task.assigned_to || '')
    setAssignError(null)
  }

  const closeAssignModal = () => {
    if (updateTaskMutation.isPending) {
      return
    }

    setAssigningTask(null)
    setSelectedAssignee('')
    setAssignError(null)
  }

  const handleAssignTask = async () => {
    if (!assigningTask || !selectedAssignee) {
      setAssignError('Please select a user to assign this task.')
      return
    }

    setAssignError(null)

    try {
      const updatedTask = await updateTaskMutation.mutateAsync({
        taskId: assigningTask.id,
        updates: { assigned_to: selectedAssignee },
      })

      await sendTaskAssignedEmailIfNeeded(updatedTask, profiles, assigningTask.assigned_to, assigningTask.status)

      setAssigningTask(null)
      setSelectedAssignee('')
    } catch (mutationError) {
      setAssignError(mutationError instanceof Error ? mutationError.message : 'Unable to assign task.')
    }
  }

  const handleDragEnd = async (event: DragEndEvent) => {
    // Sales users cannot modify tasks
    if (isSalesLikeRole(profile?.role)) {
      return
    }

    const { active, over } = event

    if (!over) {
      return
    }

    const taskId = active.id as string
    const task = tasks.find((t) => t.id === taskId)
    if (!task) {
      return
    }

    const fromColumnKey = getColumnKey(task, groupBy)
    const toColumnKey = over.id as string

    if (fromColumnKey === toColumnKey) {
      return
    }

    setWorkflowError(null)

    // Build the update payload based on groupBy type
    const updates: Record<string, unknown> = {}

    switch (groupBy) {
      case 'status':
        {
          let action: WorkflowAction | null = null

          action = action || getWorkflowActionForStatusChange(task, toColumnKey, task.assigned_to)

          if (profile?.role === 'admin' && !action) {
            const updatedTask = await updateTaskMutation.mutateAsync({
              taskId,
              expectedUpdatedAt: task.updated_at,
              updates: {
                status: toColumnKey,
              },
            })
            await sendTaskAssignedEmailIfNeeded(updatedTask, profiles, task.assigned_to, task.status)
            return
          }

          if (!action) {
            setWorkflowError(`Invalid transition from ${task.status} to ${toColumnKey}.`)
            return
          }

          const transition = transitionTask(task, action, profile, {
            users: profiles,
          })

          if (!transition.ok) {
            setWorkflowError(transition.reason)
            return
          }

          const updatedTask = await updateTaskMutation.mutateAsync({
            taskId,
            expectedUpdatedAt: task.updated_at,
            updates: transition.updates,
          })
          await sendTaskAssignedEmailIfNeeded(updatedTask, profiles, task.assigned_to, task.status)
          return
        }
        break
      case 'assigned_to':
        updates.assigned_to = toColumnKey === 'unassigned' ? null : toColumnKey
        break
      case 'type':
        updates.type = toColumnKey
        break
      case 'related_app':
        updates.related_app_id = toColumnKey === 'unknown_app' ? null : toColumnKey
        break
    }

    try {
      const updatedTask = await updateTaskMutation.mutateAsync({
        taskId,
        expectedUpdatedAt: task.updated_at,
        updates: updates as Parameters<typeof mutations.updateTask>[1],
      })
      await sendTaskAssignedEmailIfNeeded(updatedTask, profiles, task.assigned_to, task.status)
    } catch (error) {
      if (error instanceof TaskConcurrencyError) {
        setWorkflowError(error.message)
        return
      }

      throw error
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Kanban Board</h1>
          <p className="text-sm text-gray-600">Organize and move tasks across your workflow.</p>
        </div>
      </div>

      {workflowError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {workflowError}
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap gap-4 rounded-lg border border-stone-300 bg-stone-100 p-4">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700">Group by:</span>
          <div className="flex gap-2">
            {KANBAN_GROUP_BY_OPTIONS.map((option) => (
              <Button
                key={option}
                variant={groupBy === option ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setGroupBy(option)}
              >
                {KANBAN_GROUP_BY_LABELS[option]}
              </Button>
            ))}
          </div>
        </div>

        <div className="hidden h-8 w-0.5 bg-stone-400 md:block"></div>

        <div className="flex items-center gap-3">
          <Button
            variant={myTasksOnly ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => setMyTasksOnly(!myTasksOnly)}
          >
            {isSalesLikeRole(profile?.role)
              ? myTasksOnly
                ? '✓ My Created Tasks'
                : 'Show All Tasks'
              : myTasksOnly
                ? '✓ My Tasks Only'
                : 'All Tasks'}
          </Button>
        </div>

        <div className="hidden h-8 w-0.5 bg-stone-400 md:block"></div>

        <StatusPicker
          selectedStatuses={selectedStatuses}
          onToggleStatus={toggleStatus}
          className="min-w-0 flex-1"
        />
      </div>

      {/* Board */}
      {isLoading ? (
        <Card>
          <div className="flex min-h-[400px] items-center justify-center">
            <LoadingSpinner text="Loading tasks..." />
          </div>
        </Card>
      ) : error ? (
        <Card>
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error instanceof Error ? error.message : 'Failed to load tasks.'}
          </div>
        </Card>
      ) : columns.length === 0 ? (
        <Card>
          <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 text-center">
            <p className="text-base font-medium text-gray-900">No tasks available</p>
            <p className="text-sm text-gray-500">Create tasks to start organizing your workflow.</p>
          </div>
        </Card>
      ) : (
        <DndContext onDragEnd={handleDragEnd} collisionDetection={closestCorners}>
          <div className="max-h-[calc(100vh-16rem)] overflow-auto pb-4">
            <div className="flex min-w-max items-start gap-4">
              {columns.map(([columnKey, columnTasks]) => {
                const columnLabel = getColumnLabel(columnKey, groupBy, profileMap, relatedAppMap)

                return (
                  <KanbanColumn
                    key={columnKey}
                    id={columnKey}
                    title={columnLabel}
                    taskCount={columnTasks.length}
                  >
                    <SortableContext items={columnTasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-3">
                        {columnTasks.map((task) => (
                          <KanbanCard
                            key={task.id}
                            task={task}
                            profileMap={profileMap}
                            readinessState={getTaskReadinessState(task)}
                            canAssign={true}
                            onAssign={openAssignModal}
                            onView={(task) => {
                              setIsViewOnly(true)
                              setViewingTask(task)
                            }}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </KanbanColumn>
                )
              })}
            </div>
          </div>

          <DragOverlay>
            {/* Show a preview of the dragged card */}
          </DragOverlay>
        </DndContext>
      )}

      <AssignTaskModal
        task={assigningTask}
        profiles={profiles}
        selectedAssignee={selectedAssignee}
        onSelectedAssigneeChange={setSelectedAssignee}
        onClose={closeAssignModal}
        onAssign={handleAssignTask}
        isLoading={updateTaskMutation.isPending}
        error={assignError}
      />
      {viewingTask && isViewOnly && (
        <TaskViewDialog
          task={viewingTask}
          assigneeLabel={viewingTask.assigned_to ? profileMap.get(viewingTask.assigned_to) || 'Unknown user' : 'Unassigned'}
          headerSubtitle="Review task details and current delivery status."
          onClose={() => {
            setIsViewOnly(false)
            setViewingTask(null)
          }}
        />
      )}
    </div>
  )
}

export default KanbanPage
